import angularModule from 'Lessons/angularModule/scripts/lessons_module';
import template from 'Lessons/angularModule/views/stream/student_dashboard_playlists_box.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { setupStyleHelpers, setupBrandNameProperties } from 'AppBranding';
import { type IRootScopeService, type IScope } from 'angular';
import { getIsCurrentOrHasCompletedProgram, getUserProgramState, type CurrentUserIguanaObject, getCohort } from 'Users';
import { type PlaylistIguanaObject, type PlaylistClass, playlistToIguanaAttrs, deriveActivePlaylist } from 'Playlists';
import { playlistIguanaObjectToCamelCase } from 'Playlists/playlist';
import { type PublishedPlaylist } from 'FrontRoyalStore';
import { type CohortIguanaObject, type CohortClass } from 'Cohorts';
import { getCurriculumPercentComplete, getPlaylistProgress, type useContentProgressResponse } from 'ContentProgress';
import { type StudentDashboardContentResponse } from 'StudentDashboardContent';
import { streamToIguanaAttrs } from 'Lessons/stream';
import { type Stream, type StreamSnakeCasedAttrs } from 'Lessons';
import { type UserProgramState } from 'ProgramAoi';
import { type AppHeaderViewModel } from 'NavigationHelpers';
import { type ProjectsForPlaylists } from 'LearnerProjects';

import { type AnyObject } from '@Types';
import {
    type LessonIguanaObject,
    type ContentAccessHelperClass,
    type StreamIguanaClass,
    type StreamIguanaObject,
} from '../../../Lessons.types';

type Scope = IScope & {
    brandStyleClass: string;
    currentUser: CurrentUserIguanaObject;
    relevantCohort: CohortIguanaObject;
    playlists: PlaylistIguanaObject[];
    streams: StreamIguanaObject[];
    activePlaylist: PlaylistIguanaObject | null;
    activatePlaylist: (playlist: PlaylistIguanaObject) => void;
    recommendedPlaylist: PlaylistIguanaObject | null;
    defaultPlaylistIcon: PlaylistClass['DEFAULT_ICON'];
    concentrationPlaylists: (PublishedPlaylist | PlaylistIguanaObject)[];
    numUnlockedConcentrationPlaylists: number;
    switchLearningBoxMode: (mode: 'playlists' | 'active_playlist') => void;
    studentDashboardContent: StudentDashboardContentResponse;
    prefUps: UserProgramState;
    keepLearningStream: StreamIguanaObject | null;
    readyToShowPlaylistMap: boolean;
    keepLearningLesson: LessonIguanaObject | null;
    modifyHeaderSection: boolean;
    playlistsPercentComplete: number;
    playlistReferencesNeedUpdatingAfterProgressUpdate?: boolean;
    contentProgress: useContentProgressResponse;
    projectsForPlaylists: ProjectsForPlaylists;
};

type RootScope = IRootScopeService & {
    currentUser: CurrentUserIguanaObject;
};

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('studentDashboardPlaylistsBox', [
    '$injector',

    function factory($injector: ng.auto.IInjectorService) {
        const $rootScope = $injector.get<RootScope>('$rootScope');
        const Playlist = $injector.get<PlaylistClass>('Playlist');
        const Cohort = $injector.get<CohortClass>('Cohort');
        const Stream = $injector.get<StreamIguanaClass>('Lesson.Stream');
        const ContentAccessHelper = $injector.get<ContentAccessHelperClass>('ContentAccessHelper');
        const AppHeaderViewModel = $injector.get<AppHeaderViewModel>('Navigation.AppHeader.AppHeaderViewModel');

        return {
            restrict: 'E',
            templateUrl,
            scope: {
                switchLearningBoxMode: '<',
                studentDashboardContent: '<?',
                contentProgress: '<?',
                projectsForPlaylists: '<?',
                modifyHeaderSection: '<',
            },
            link(scope: Scope) {
                scope.defaultPlaylistIcon = Playlist.DEFAULT_ICON;
                scope.readyToShowPlaylistMap = false;
                scope.playlistReferencesNeedUpdatingAfterProgressUpdate = scope.modifyHeaderSection;

                Object.defineProperty(scope, 'prefUps', {
                    get() {
                        return getUserProgramState(scope.currentUser);
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        return $rootScope.currentUser;
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'relevantCohort', {
                    get() {
                        return scope.currentUser?.relevantCohort;
                    },
                    configurable: true,
                });

                function isPlaylistComplete(playlist: PlaylistIguanaObject): boolean {
                    const playlistProgress = getPlaylistProgress(
                        playlistIguanaObjectToCamelCase(playlist),
                        scope.contentProgress,
                        scope.projectsForPlaylists,
                    );
                    return playlistProgress.complete;
                }

                Object.defineProperty(scope, 'showCompletePlaylistsHeader', {
                    get() {
                        if (scope.modifyHeaderSection) return false;

                        return !!(
                            scope.activePlaylist &&
                            isPlaylistComplete(scope.activePlaylist) &&
                            scope.recommendedPlaylist &&
                            scope.activePlaylist !== scope.recommendedPlaylist
                        );
                    },
                });

                scope.$watch('playlists', (playlists: PlaylistIguanaObject[]) => {
                    if (!playlists || playlists.length === 0) {
                        scope.playlistsPercentComplete = 0;
                        return;
                    }

                    try {
                        const cohort = getCohort(scope.currentUser);
                        if (!cohort || !scope.contentProgress || !scope.projectsForPlaylists) {
                            scope.playlistsPercentComplete = 0;
                            return;
                        }

                        const camelCasePlaylists = playlists.map(p => playlistIguanaObjectToCamelCase(p)!);
                        scope.playlistsPercentComplete = getCurriculumPercentComplete(
                            cohort,
                            camelCasePlaylists,
                            scope.projectsForPlaylists,
                            scope.contentProgress,
                        );
                    } catch {
                        // This can temporarily happen when the user changes cohorts via their ups
                        // It does not cause a visual issue, but can trigger an error
                        scope.playlistsPercentComplete = 0;
                    }
                });

                Object.defineProperty(scope, 'playlistsViewDescriptionLocaleKey', {
                    get() {
                        // FIXME: seems like the relevantCohort?.learningBoxDescriptionKey should
                        // be unnecessary, but MiyaMiya users have the "external" programType for some reason
                        return (
                            scope.relevantCohort?.learningBoxDescriptionKey ||
                            Cohort.learningBoxDescriptionKey(scope.currentUser?.programType) ||
                            'description_playlists'
                        );
                    },
                });

                Object.defineProperty(scope, 'playlistsViewTitleKey', {
                    get() {
                        // FIXME: seems like the relevantCohort?.learningBoxDescriptionKey should
                        // be unnecessary, but MiyaMiya users have the "external" programType for some reason
                        return (
                            scope.relevantCohort?.learningBoxTitleKey ||
                            Cohort.learningBoxTitleKey(scope.currentUser?.programType) ||
                            'playlists'
                        );
                    },
                });

                Object.defineProperty(scope, 'programTitle', {
                    get() {
                        return scope.relevantCohort?.standaloneShortProgramTitle;
                    },
                });

                function updateActivePlaylistForLocalePackId() {
                    scope.activePlaylist = deriveActivePlaylist(scope.playlists, scope.currentUser);
                }

                scope.$watchGroup(
                    ['studentDashboardContent'],
                    ([content]: [StudentDashboardContentResponse], [oldContent]) => {
                        const newContentAdded =
                            content.lessonStreams.length !== oldContent.lessonStreams.length ||
                            content.availablePlaylists.length !== oldContent.availablePlaylists.length;
                        // Because of how we are using readyToShowPlaylistMap to prevent
                        // lots of re-renders/updates, we need to set it to false when new
                        // content is added. This is really only a problem when a user gets a new UPS
                        // and the number of playlists/streams changes.
                        if (newContentAdded) {
                            scope.readyToShowPlaylistMap = false;
                        }
                    },
                );

                scope.$watchGroup(
                    ['studentDashboardContent', 'contentProgress', 'readyToShowPlaylistMap'],
                    ([content, progress, readyToShowPlaylistMap]: [
                        StudentDashboardContentResponse,
                        useContentProgressResponse,
                        boolean,
                    ]) => {
                        if (!content || !progress) return;
                        if (readyToShowPlaylistMap) return;

                        scope.recommendedPlaylist = null;
                        const {
                            streamProgressByLocalePackId,
                            lessonProgressByLocalePackId,
                            iguanaStreamProgressByLocalePackId,
                            iguanaLessonProgressByLocalePackId,
                        } = progress;

                        scope.streams = content.lessonStreams.map(stream => {
                            const streamProgress = streamProgressByLocalePackId[stream.localePackId];

                            const cachedStream = Stream.getCachedForLocalePackId(stream.localePackId, false);

                            if (cachedStream) {
                                const iguanaStreamProgress = iguanaStreamProgressByLocalePackId[stream.localePackId];

                                cachedStream.lesson_streams_progress = iguanaStreamProgress;
                                if (cachedStream.lesson_streams_progress) {
                                    (cachedStream.lesson_streams_progress as AnyObject).$$embeddedIn = cachedStream;
                                }
                                cachedStream.lessons.forEach(lesson => {
                                    const iguanaLesson = lesson as unknown as LessonIguanaObject<true>;
                                    iguanaLesson.lesson_progress =
                                        iguanaLessonProgressByLocalePackId[iguanaLesson.locale_pack.id];
                                });

                                return cachedStream;
                            }

                            // This is dangerous. `stream` here is an instance of StreamForDashboard. When
                            // we cast it to Stream, we are promising that it has properties that it does
                            // not in fact have. But, this is legacy code and we don't want to split the
                            // StreamIguanaInstance into multiple types.
                            return Stream.new(
                                streamToIguanaAttrs(
                                    stream as unknown as Stream,
                                    streamProgress,
                                    lessonProgressByLocalePackId,
                                ) as StreamSnakeCasedAttrs<false>,
                            );
                        });

                        scope.playlists = content.availablePlaylists.map(playlist => {
                            const cachedPlaylist = Playlist.getCachedForLocalePackId(playlist.localePackId, false);

                            if (cachedPlaylist) {
                                return cachedPlaylist;
                            }

                            return Playlist.new(playlistToIguanaAttrs(playlist));
                        });

                        updateActivePlaylistForLocalePackId();

                        scope.concentrationPlaylists = scope.relevantCohort
                            ? scope.relevantCohort.getConcentrationPlaylists(scope.playlists)
                            : scope.playlists;

                        scope.numUnlockedConcentrationPlaylists = scope.concentrationPlaylists.filter(p =>
                            ContentAccessHelper.canLaunch(p as PlaylistIguanaObject),
                        ).length;

                        const hasSinglePlaylist = scope.playlists && scope.playlists.length === 1;

                        if (hasSinglePlaylist && AppHeaderViewModel.learningBoxMode) {
                            AppHeaderViewModel.learningBoxMode.hasSinglePlaylist = true;
                        }

                        if (scope.activePlaylist && isPlaylistComplete(scope.activePlaylist)) {
                            const specialization_lpids = scope.relevantCohort
                                ? scope.relevantCohort.specialization_playlist_pack_ids || []
                                : [];

                            // Don't recommend complete nor specialization playlists (the user chooses those)
                            let recommendablePlaylists = scope.playlists.filter(p => {
                                if (isPlaylistComplete(p)) return false;
                                if (specialization_lpids.includes(p.localePackId)) return false;
                                return ContentAccessHelper.canLaunch(p);
                            });

                            if (scope.relevantCohort) {
                                // Do not recommend the Business Foundations playlist to
                                // accepted users in a cohort that should exclude the
                                // foundations playlist on acceptance.
                                if (
                                    getIsCurrentOrHasCompletedProgram(scope.currentUser) &&
                                    Cohort.excludesFoundationsPlaylistOnAcceptance(scope.currentUser.programType)
                                ) {
                                    const foundationsPlaylist = recommendablePlaylists.find(
                                        playlist =>
                                            playlist.localePackId ===
                                            scope.relevantCohort.foundationsPlaylistLocalePackId,
                                    );
                                    if (foundationsPlaylist) {
                                        recommendablePlaylists = recommendablePlaylists.filter(
                                            p => p.localePackId !== foundationsPlaylist.localePackId,
                                        );
                                    }
                                }

                                // use the playlist order defined by the cohort
                                scope.recommendedPlaylist = [...recommendablePlaylists].sort((a, b) => {
                                    const indexA = scope.relevantCohort.concentrationPlaylistPackIds.indexOf(
                                        a.localePackId,
                                    );
                                    const indexB = scope.relevantCohort.concentrationPlaylistPackIds.indexOf(
                                        b.localePackId,
                                    );
                                    const safeIndexA = indexA > -1 ? indexA : 9999;
                                    const safeIndexB = indexB > -1 ? indexB : 9999;

                                    return safeIndexA - safeIndexB;
                                })[0];
                            } else {
                                scope.recommendedPlaylist = [...recommendablePlaylists].sort(
                                    (a, b) => b.percentComplete - a.percentComplete,
                                )[0];
                            }
                        }
                        scope.readyToShowPlaylistMap = true;
                    },
                );

                scope.activatePlaylist = (playlist: PlaylistIguanaObject) => {
                    if (
                        playlist &&
                        scope.activePlaylist &&
                        playlist.id === scope.activePlaylist.id &&
                        playlist.localePackId === scope.currentUser.active_playlist_locale_pack_id
                    ) {
                        scope.switchLearningBoxMode('active_playlist');
                        return;
                    }

                    scope.activePlaylist = playlist;

                    if (scope.prefUps) {
                        scope.prefUps.activePlaylistLocalePackId = playlist.localePackId;
                    }
                    scope.currentUser.active_playlist_locale_pack_id = playlist.localePackId;

                    // sometimes we log in as users and change their active playlist
                    // for them, thus the lack of checking for ghostMode
                    scope.currentUser.save();

                    scope.switchLearningBoxMode('active_playlist');
                    scope.readyToShowPlaylistMap = false;
                };

                // Watch for the user's active playlist to change in push messages, which
                // can happen if they are accepted into a cohort. See https://trello.com/c/CH2RajXg
                scope.$watch('prefUps.activePlaylistLocalePackId', newVal => {
                    if (newVal && scope.playlists) {
                        updateActivePlaylistForLocalePackId();
                    }
                });

                // If the user has a relevant cohort, then the courses shown here are coming
                // from that cohort, so it makes sense to use that as the branding.  If the
                // user does not have a relevant cohort, this will fall back to the user's
                // branding, or if there is no user then the domain's.
                const brandingOpts = { branding: () => scope.relevantCohort?.branding };
                setupBrandNameProperties($injector, scope, brandingOpts);
                setupStyleHelpers($injector, scope, brandingOpts);
            },
        };
    },
]);
